import { useState } from "react";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  rem,
  Select,
  Stack,
  Text,
  TextInput,
  Badge,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconPackage,
  IconCheck,
  IconSearch,
  IconPlus,
} from "@tabler/icons-react";
import { ItemCard, ItemCardCompact, ItemRarity } from "../ItemCard";

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: rem(500),
  },
  header: {
    marginBottom: theme.spacing.md,
  },
  headerIcon: {
    width: rem(48),
    height: rem(48),
    borderRadius: theme.radius.md,
    backgroundColor: `${theme.colors.purple[8]}20`,
    border: `1px solid ${theme.colors.purple[8]}40`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colors.purple[4],
  },
  headerTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  headerSubtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[3],
  },
  searchCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  inventoryCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.lg,
    overflow: "hidden",
  },
  inventoryHeader: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.dark[7],
    borderBottom: `1px solid ${theme.colors.dark[5]}`,
  },
  inventoryBody: {
    padding: theme.spacing.md,
    maxHeight: rem(400),
    overflowY: "auto",
  },
  addItemCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.lg,
    overflow: "hidden",
    marginTop: theme.spacing.md,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing.xl,
    color: theme.colors.dark[3],
  },
  itemGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: theme.spacing.md,
  },
  saveButton: {
    fontWeight: 600,
    marginTop: theme.spacing.md,
  },
  loaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: rem(200),
  },
  statsRow: {
    backgroundColor: theme.colors.dark[7],
    borderRadius: theme.radius.md,
    padding: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  statItem: {
    textAlign: "center",
  },
  statValue: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 700,
    color: theme.colors.cyan[4],
  },
  statLabel: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },
}));

const ITEMS_QUERY = gql`
  query ItemsForInventory {
    items(page: { first: 500 }) {
      nodes {
        id
        name
        description
        categoryId
        metadata {
          property
          value
        }
      }
    }
  }
`;

const ITEM_CATEGORIES_QUERY = gql`
  query ItemCategoriesForInventory {
    itemCategories(page: { first: 100 }) {
      nodes {
        id
        name
      }
    }
  }
`;

const PLAYER_ITEMS_QUERY = gql`
  query PlayerItems($playerId: ID!) {
    playerItems(
      filter: { playerProfileIds: [$playerId] }
      page: { first: 500 }
    ) {
      nodes {
        id
        itemId
        quantity
        metadata {
          property
          value
        }
      }
    }
  }
`;

const UPDATE_PLAYER_ITEMS_MUTATION = gql`
  mutation UpdatePlayerItems($playerId: ID!, $input: PlayerItemsUpdateInput!) {
    playerItemsUpdate(playerId: $playerId, input: $input) {
      id
      itemId
      quantity
    }
  }
`;

interface InventoryChange {
  itemId: string;
  originalQuantity: number;
  newQuantity: number;
}

interface PlayerInventoryManagerProps {
  playerId: string;
  playerName: string;
  onSuccess?: () => void;
}

// Helper to extract rarity from metadata
const getRarityFromMetadata = (
  metadata: Array<{ property: string; value: string }> | undefined,
): ItemRarity => {
  const rarityMeta = metadata?.find(
    (m) => m.property.toLowerCase() === "rarity",
  );
  if (rarityMeta) {
    const value = rarityMeta.value.toLowerCase();
    if (["common", "uncommon", "rare", "epic", "legendary"].includes(value)) {
      return value as ItemRarity;
    }
  }
  return "common";
};

export const PlayerInventoryManager = ({
  playerId,
  playerName,
  onSuccess,
}: PlayerInventoryManagerProps) => {
  const { classes } = useStyles();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [inventoryChanges, setInventoryChanges] = useState<
    Record<string, InventoryChange>
  >({});
  const [submitting, setSubmitting] = useState(false);

  const { data: itemsData, loading: itemsLoading } = useQuery<any>(ITEMS_QUERY);
  const { data: categoriesData, loading: categoriesLoading } = useQuery<any>(
    ITEM_CATEGORIES_QUERY,
  );
  const {
    data: playerItemsData,
    loading: playerItemsLoading,
    refetch,
  } = useQuery<any>(PLAYER_ITEMS_QUERY, {
    variables: { playerId },
    skip: !playerId,
  });

  const [updatePlayerItems] = useMutation<any>(UPDATE_PLAYER_ITEMS_MUTATION);

  const items = itemsData?.items?.nodes || [];
  const categories = categoriesData?.itemCategories?.nodes || [];
  const playerItems = playerItemsData?.playerItems?.nodes || [];

  // Build a map of itemId -> quantity
  const playerInventory = new Map<string, number>();
  playerItems.forEach((pi: any) => {
    playerInventory.set(pi.itemId, pi.quantity);
  });

  // Get current quantity (considering changes)
  const getCurrentQuantity = (itemId: string): number => {
    if (inventoryChanges[itemId]) {
      return inventoryChanges[itemId].newQuantity;
    }
    return playerInventory.get(itemId) || 0;
  };

  // Handle quantity change
  const handleQuantityChange = (itemId: string, delta: number) => {
    const originalQuantity = playerInventory.get(itemId) || 0;
    const currentChange = inventoryChanges[itemId];
    const currentQuantity = currentChange
      ? currentChange.newQuantity
      : originalQuantity;
    const newQuantity = Math.max(0, currentQuantity + delta);

    if (newQuantity === originalQuantity) {
      // Remove from changes if back to original
      const { [itemId]: _, ...rest } = inventoryChanges;
      setInventoryChanges(rest);
    } else {
      setInventoryChanges({
        ...inventoryChanges,
        [itemId]: {
          itemId,
          originalQuantity,
          newQuantity,
        },
      });
    }
  };

  // Filter items based on search and category
  const filteredItems = items.filter((item: any) => {
    const matchesSearch =
      !searchQuery ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      !selectedCategoryId || item.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });

  // Get items in player's inventory
  const inventoryItems = filteredItems.filter(
    (item: any) => getCurrentQuantity(item.id) > 0,
  );

  // Get items not in inventory (for adding)
  const availableItems = filteredItems.filter(
    (item: any) => getCurrentQuantity(item.id) === 0,
  );

  const hasChanges = Object.keys(inventoryChanges).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;

    setSubmitting(true);
    try {
      const itemsToUpdate = Object.values(inventoryChanges).map((change) => {
        const diff = change.newQuantity - change.originalQuantity;
        if (diff > 0) {
          return { itemId: change.itemId, add: diff };
        } else if (diff < 0) {
          return { itemId: change.itemId, remove: Math.abs(diff) };
        }
        return { itemId: change.itemId, set: change.newQuantity };
      });

      const result = await updatePlayerItems({
        variables: {
          playerId,
          input: {
            items: itemsToUpdate,
          },
        },
      });

      if (result?.error) {
        throw new Error(result.error.message);
      }

      notifications.show({
        title: "Inventory Updated",
        message: `${playerName}'s inventory has been updated`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      setInventoryChanges({});
      refetch();
      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: "Update Failed",
        message:
          error instanceof Error ? error.message : "Failed to update inventory",
        color: "rose",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const loading = itemsLoading || categoriesLoading || playerItemsLoading;

  if (loading) {
    return (
      <Box className={classes.loaderContainer}>
        <Loader color="purple" />
      </Box>
    );
  }

  const categoryOptions = [
    { value: "", label: "All Categories" },
    ...categories.map((cat: any) => ({
      value: cat.id,
      label: cat.name,
    })),
  ];

  const totalItems = inventoryItems.reduce(
    (sum: number, item: any) => sum + getCurrentQuantity(item.id),
    0,
  );
  const uniqueItems = inventoryItems.length;
  const changesCount = Object.keys(inventoryChanges).length;

  return (
    <Stack className={classes.container}>
      {/* Header */}
      <Group className={classes.header}>
        <Box className={classes.headerIcon}>
          <IconPackage size={24} />
        </Box>
        <div>
          <Text className={classes.headerTitle}>Inventory Manager</Text>
          <Text className={classes.headerSubtitle}>
            Manage {playerName}'s items
          </Text>
        </div>
      </Group>

      {/* Stats Row */}
      <Group className={classes.statsRow} grow>
        <Box className={classes.statItem}>
          <Text className={classes.statValue}>{uniqueItems}</Text>
          <Text className={classes.statLabel}>Unique Items</Text>
        </Box>
        <Box className={classes.statItem}>
          <Text className={classes.statValue}>{totalItems}</Text>
          <Text className={classes.statLabel}>Total Quantity</Text>
        </Box>
        <Box className={classes.statItem}>
          <Text className={classes.statValue}>{changesCount}</Text>
          <Text className={classes.statLabel}>Pending Changes</Text>
        </Box>
      </Group>

      {/* Search & Filter */}
      <Card className={classes.searchCard}>
        <Group grow>
          <TextInput
            leftSection={<IconSearch size={16} />}
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Select
            placeholder="Filter by category"
            data={categoryOptions}
            value={selectedCategoryId || ""}
            onChange={(value) => setSelectedCategoryId(value || null)}
            clearable
          />
        </Group>
      </Card>

      {/* Current Inventory */}
      <Card className={classes.inventoryCard} p={0}>
        <Box className={classes.inventoryHeader}>
          <Group justify="space-between">
            <Group gap="sm">
              <IconPackage size={18} />
              <Text fw={600}>Current Inventory</Text>
            </Group>
            {hasChanges && (
              <Badge color="amber" variant="light">
                {changesCount} changes pending
              </Badge>
            )}
          </Group>
        </Box>
        <Box className={classes.inventoryBody}>
          {inventoryItems.length === 0 ? (
            <Box className={classes.emptyState}>
              <IconPackage size={48} stroke={1.5} />
              <Text mt="md">No items in inventory</Text>
              <Text size="sm" c="dimmed">
                Add items from the catalog below
              </Text>
            </Box>
          ) : (
            <Box className={classes.itemGrid}>
              {inventoryItems.map((item: any) => {
                const category = categories.find(
                  (c: any) => c.id === item.categoryId,
                );
                const rarity = getRarityFromMetadata(item.metadata);
                const hasChange = !!inventoryChanges[item.id];

                return (
                  <Box key={item.id} style={{ position: "relative" }}>
                    {hasChange && (
                      <Badge
                        color="amber"
                        size="xs"
                        style={{
                          position: "absolute",
                          top: -8,
                          left: -8,
                          zIndex: 10,
                        }}
                      >
                        Modified
                      </Badge>
                    )}
                    <ItemCard
                      id={item.id}
                      name={item.name}
                      description={item.description}
                      category={category?.name}
                      quantity={getCurrentQuantity(item.id)}
                      rarity={rarity}
                      showQuantityControls
                      onQuantityChange={handleQuantityChange}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Card>

      {/* Add Items */}
      <Card className={classes.addItemCard} p={0}>
        <Box className={classes.inventoryHeader}>
          <Group gap="sm">
            <IconPlus size={18} />
            <Text fw={600}>Add Items to Inventory</Text>
          </Group>
        </Box>
        <Box className={classes.inventoryBody}>
          {availableItems.length === 0 ? (
            <Box className={classes.emptyState}>
              <Text size="sm" c="dimmed">
                {searchQuery || selectedCategoryId
                  ? "No items match your search"
                  : "All items are already in inventory"}
              </Text>
            </Box>
          ) : (
            <Stack gap="xs">
              {availableItems.slice(0, 10).map((item: any) => {
                const rarity = getRarityFromMetadata(item.metadata);

                return (
                  <ItemCardCompact
                    key={item.id}
                    id={item.id}
                    name={item.name}
                    description={item.description}
                    quantity={0}
                    rarity={rarity}
                    showQuantityControls
                    onQuantityChange={handleQuantityChange}
                  />
                );
              })}
              {availableItems.length > 10 && (
                <Text size="sm" c="dimmed" ta="center">
                  And {availableItems.length - 10} more items...
                </Text>
              )}
            </Stack>
          )}
        </Box>
      </Card>

      {/* Save Button */}
      {hasChanges && (
        <Button
          className={classes.saveButton}
          loading={submitting}
          onClick={handleSave}
          leftSection={<IconCheck size={16} />}
          fullWidth
        >
          Save Changes ({changesCount} item{changesCount !== 1 ? "s" : ""})
        </Button>
      )}
    </Stack>
  );
};

// Helper to open the modal
export const openPlayerInventoryModal = (
  props: PlayerInventoryManagerProps,
) => {
  modals.open({
    title: null,
    centered: true,
    size: "xl",
    children: <PlayerInventoryManager {...props} />,
  });
};
